import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../util/API';

const initialState = {
    orders: [],
    order: null,
    status: 'idle',
    error: null,
};

// Async thunk to fetch all orders
export const fetchOrders = createAsyncThunk(
    'order/fetchOrders',
    async (_, { rejectWithValue }) => {
        try {
            const response = await API.get('/order');
            return response.data?.data ?? response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Async thunk to fetch a single order by ID
export const fetchOrderById = createAsyncThunk(
    'order/fetchOrderById',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await API.get(`/order/${orderId}`);
            return response.data?.data ?? response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// Async thunk to create a new order
export const createOrder = createAsyncThunk(
    'order/createOrder',
    async (orderData, { rejectWithValue }) => {
        try {
            const response = await API.post('/order', orderData);
            return response.data?.data ?? response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        clearOrder(state) {
            state.order = null;
            state.error = null;
        },
        setCurrentOrder(state, action) {
            state.order = action.payload || null;
            if (action.payload) {
                const existing = Array.isArray(state.orders) ? state.orders : [];
                const incomingId = String(action.payload?._id || action.payload?.orderId || "");
                if (incomingId) {
                    const without = existing.filter((o) => String(o?._id || o?.orderId || "") !== incomingId);
                    state.orders = [action.payload, ...without];
                } else {
                    state.orders = [action.payload, ...existing];
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchOrders
            .addCase(fetchOrders.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchOrders.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.orders = action.payload;
            })
            .addCase(fetchOrders.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // fetchOrderById
            .addCase(fetchOrderById.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchOrderById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.order = action.payload;
            })
            .addCase(fetchOrderById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // createOrder
            .addCase(createOrder.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.orders.push(action.payload);
                state.order = action.payload;
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
    },
});

export const { clearOrder, setCurrentOrder } = orderSlice.actions;

export default orderSlice.reducer;
